/**
 * Context de autenticación para React
 * Maneja login, logout, y estado de usuario
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient, { refreshAccessToken } from '@/lib/axiosInstance';
import { setUserRole, clearUserRole } from '@/lib/userRole';
import { clearSessionData, getAccessToken, storeTokens } from '@/lib/session';

// Un solo cliente HTTP (FE-11): este contexto usaba `fetch` crudo con la
// cabecera Authorization puesta a mano y su propia URL base. Ahora va por
// `apiClient`, que ya inyecta el token y centraliza tiempos de espera,
// reintentos de 429 y el refresco. Las rutas van relativas: la base
// `${BACKEND_URL}/api` la pone axios.
//
// La cookie httpOnly `access_token` que pone el backend se queda como estaba:
// ni el `fetch` anterior ponía `credentials: 'include'` ni aquí se toca
// `withCredentials`. El navegador la manda sola cuando el origen coincide, que
// es el caso en el despliegue real (mismo dominio, el proxy enruta /api).
// Poner `withCredentials` aquí cambiaría el envío de cookies de TODA la
// aplicación, y eso no es parte de unificar el cliente.

// El detalle del error llega distinto según el cliente: `fetch` obligaba a
// leer el cuerpo a mano; axios ya lo trae en `err.response.data`.
const errorDetail = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return err?.message || fallback;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar token al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = getAccessToken();

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const { data: userData } = await apiClient.get('/auth/me');
        setUser(userData);
        // El rol se derivaba SOLO de la pantalla de inicio de sesión, que
        // escribe sessionStorage — por pestaña. El token vive en
        // localStorage — por navegador. En una pestaña nueva el token
        // sobrevivía y el rol no, así que un administrador veía la
        // aplicación sin botones de acción y sin ningún aviso. Aquí ya
        // teníamos la respuesta de /auth/me y se estaba tirando el rol.
        setUserRole(userData?.role);
      } catch (err) {
        // silencio-ok: red caída no cierra la sesión; cada pantalla avisa del fallo en su propia carga
        // Un 401 que llega hasta aquí ya pasó por el interceptor de
        // `apiClient`, que intentó renovar el token y repetir la petición. Si
        // aun así llega, la sesión ha terminado de verdad: se limpia el estado
        // de React (el interceptor ya borró los tokens y navegó a /login).
        if (err?.response?.status === 401) {
          logout();
        }
        // Cualquier otro estado (502 durante un despliegue, 5xx, 429 agotado,
        // red caída) NO dice nada de la sesión: se conserva y la app reintenta
        // en la siguiente petición. Esto NO es motivo para cerrar sesión.
        console.error('Auth check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    setError(null);
    
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      // `/auth/login` espera x-www-form-urlencoded (OAuth2PasswordRequestForm
      // de FastAPI), no JSON. Axios pone esa cabecera solo al ver un
      // URLSearchParams, así que se declara explícitamente para que no
      // dependa de ese detalle.
      const { data } = await apiClient.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      storeTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      setUser(data.user);
      setUserRole(data.user?.role);

      return data;
    } catch (err) {
      const message = errorDetail(err, 'Login failed');
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (email, password, fullName) => {
    setError(null);
    
    try {
      const { data } = await apiClient.post('/auth/register', {
        email,
        password,
        full_name: fullName,
      });

      storeTokens({ accessToken: data.access_token, refreshToken: data.refresh_token });
      setUser(data.user);
      setUserRole(data.user?.role);

      return data;
    } catch (err) {
      const message = errorDetail(err, 'Registration failed');
      setError(message);
      throw new Error(message);
    }
  };

  const logout = useCallback(() => {
    clearSessionData();
    setUser(null);
    clearUserRole();
  }, []);

  // No implementa el refresco: lo delega en el ÚNICO camino, el de
  // `lib/axiosInstance` (FE-11). Antes había aquí una segunda copia con
  // `fetch` crudo, con su propio criterio de cuándo cerrar la sesión y su
  // propia escritura de `access_token`.
  const refreshToken = async () => {
    const result = await refreshAccessToken();

    if (result.status === 'expired') {
      // El servidor rechaza el refresh token (o no hay): la sesión terminó.
      logout();
      return null;
    }
    if (result.status !== 'ok') {
      // 5xx / 502 de un despliegue o red caída: la respuesta no dice nada de
      // la sesión, se conserva y se reintenta en la siguiente petición.
      return null;
    }

    // Usuario actualizado con el token nuevo. Va por `apiClient`, que ya lo
    // lee de localStorage (lo acaba de escribir `refreshAccessToken`).
    try {
      const { data: userData } = await apiClient.get('/auth/me');
      setUser(userData);
      setUserRole(userData?.role);
    } catch (err) {
      // silencio-ok: el token ya está renovado; el perfil se reintenta en la siguiente carga y cada pantalla avisa de su propio fallo
      console.error('No se pudo releer el perfil tras renovar el token:', err);
    }

    return result.token;
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshToken,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
